import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Check,
  Info,
  Mail,
  Search,
  Shield,
  User,
  Users,
} from "lucide-react";

import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../../components/StorageHelper";
import useUserStore from "../../../services/userStore";
import useNotificationStore from "../../../services/notificationStore";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import useCloudSyncWatcher from "../../../services/useCloudSyncWatcher";
import { triggerCloudSync } from "../../../triggerCloudSync";
import CloudFilters from "../../../components/v2components/CloudFilters";

const DetailRow = ({ label, value }) => (
  <div className="rounded-2xl border border-border bg-card p-3 shadow-lg shadow-sky-500/5">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 break-words text-sm font-medium text-foreground">{value || "N/A"}</div>
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-8 text-center shadow-lg shadow-sky-500/5">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-border/50 bg-card shadow-sm">
      <Icon className="h-7 w-7 text-muted-foreground" />
    </div>
    <h3 className="text-base font-semibold text-foreground">{title}</h3>
    <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
  </div>
);

const SkeletonList = () => (
  <div className="space-y-3">
    {[1, 2, 3, 4, 5].map((item) => (
      <div key={item} className="animate-pulse rounded-2xl border border-border bg-card p-4 shadow-lg shadow-sky-500/5">
        <div className="mb-3 h-4 w-1/2 rounded bg-muted/60" />
        <div className="h-3 w-3/4 rounded bg-muted/40" />
      </div>
    ))}
  </div>
);

const gridClass = "grid grid-cols-1 gap-5 xl:grid-cols-3";
const panelClass = "flex min-h-[calc(100vh-250px)] flex-col overflow-hidden rounded-2xl border portal-card-elevated bg-card p-4";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm:ss") : "N/A";
};

const getStoredTenantName = () => {
  const stored = myLocalStorage.getItem("latestTenant");
  return typeof stored === "string" ? stored : stored?.tenantName || "";
};

const getStoredSubscriptionId = () => {
  const stored = myLocalStorage.getItem("latestSubList");
  if (!stored) return "";
  if (typeof stored === "string") {
    try {
      return JSON.parse(stored)?.azureSubscriptionId || "";
    } catch {
      return "";
    }
  }
  return stored?.azureSubscriptionId || "";
};

const normalizeGroupsResponse = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.groups)) return payload.groups;
  if (Array.isArray(payload?.azureGroups)) return payload.azureGroups;
  return [];
};

const AzureGroups = () => {
  const userData = useUserStore((state) => state.user);
  const syncStatus = useCloudSyncStore((state) => state.syncStatus);
  const { azureConfig } = useNotificationStore.getState();

  const [selectedTenant, setSelectedTenant] = useState(getStoredTenantName());
  const [selectedSubscription, setSelectedSubscription] = useState(getStoredSubscriptionId());
  const [searchTerm, setSearchTerm] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupUsers, setGroupUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const selectedGroupId = selectedGroup?.id;

  const fetchGroupUsers = useCallback(async (group) => {
    if (!group?.id) {
      setGroupUsers([]);
      return;
    }

    setUsersLoading(true);
    try {
      const response = await axios.get(`${NGROK}/api/azure/v1/group-users`, {
        params: { groupId: group.id },
      });
      setGroupUsers(response.data || []);
    } catch (error) {
      console.error("Failed to fetch Azure group users:", error);
      setGroupUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async (initialLoad = false) => {
    if (!selectedTenant || !selectedSubscription) {
      setGroups([]);
      setSelectedGroup(null);
      setGroupUsers([]);
      setLoading(false);
      return;
    }

    if (initialLoad) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${NGROK}/api/azure/v1/groups`, {
        params: { tenantName: selectedTenant },
      });
      const data = normalizeGroupsResponse(response.data);
      setGroups(data);

      setSelectedGroup((current) => {
        if (!data.length) return null;
        const existing = current ? data.find((group) => group.id === current.id) : null;
        return existing || (initialLoad ? data[0] : data[0]);
      });
    } catch (error) {
      console.error("Failed to fetch Azure groups:", error);
      setGroups([]);
      setSelectedGroup(null);
      setGroupUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedSubscription, selectedTenant]);

  const handleSync = async () => {
    try {
      await triggerCloudSync("azure", "/api/azure-sync/onDemand", selectedTenant, () => fetchGroups(false));
    } catch (error) {
      console.error("Azure sync failed:", error);
    }
  };

  useCloudSyncWatcher("azure", selectedTenant, () => fetchGroups(false));

  useEffect(() => {
    fetchGroups(true);
  }, [fetchGroups]);

  useEffect(() => {
    if (!selectedTenant) return undefined;
    const interval = setInterval(() => {
      fetchGroups(false);
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchGroups, selectedTenant]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchGroupUsers({ id: selectedGroupId });
      setSelectedUser(null);
      setMemberSearch("");
      setActiveTab("details");
    } else {
      setGroupUsers([]);
      setSelectedUser(null);
    }
  }, [fetchGroupUsers, selectedGroupId]);

  const filteredGroups = useMemo(() => {
    const query = searchTerm.toLowerCase();
    return groups.filter((group) =>
      [group.displayName, group.mail, group.mailNickname]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [groups, searchTerm]);

  const filteredUsers = useMemo(() => {
    const query = memberSearch.toLowerCase();
    return (groupUsers || []).filter((user) =>
      [user.displayName, user.mail, user.userPrincipalName, user.azureId]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [groupUsers, memberSearch]);

  const handleTenantChange = (tenant) => {
    if (tenant === selectedTenant) return;
    setSelectedTenant(tenant);
    setGroups([]);
    setSelectedGroup(null);
    setSelectedUser(null);
  };

  const handleSubscriptionChange = (subscription) => {
    if (subscription === selectedSubscription) return;
    setSelectedSubscription(subscription);
  };

  return (
    <div className="h-full bg-background p-6">
      <CloudFilters
        cloudProvider="azure"
        onTenantChange={handleTenantChange}
        onCloudAccountChange={handleSubscriptionChange}
        isAdmin={userData.role !== "TENANT_USER"}
        searchQuery={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search groups..."
        onSync={handleSync}
        syncDisabled={!azureConfig || syncStatus.azure || !selectedTenant || !selectedSubscription}
        syncLoading={Boolean(selectedSubscription) && syncStatus.azure}
      />

      <div className={gridClass}>
        <section className={panelClass}>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-base font-semibold text-foreground">Groups</h2>
              <p className="text-xs text-muted-foreground">{filteredGroups.length} of {groups.length} groups</p>
            </div>
            <div className="rounded-xl bg-primary/10 p-2 text-primary">
              <Users className="h-4 w-4" />
            </div>
          </div>

          {loading ? (
            <SkeletonList />
          ) : filteredGroups.length === 0 ? (
            <EmptyState icon={Users} title="No Azure groups found" description="No groups match this tenant, subscription, or search." />
          ) : (
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1 scrollbar-thin">
              {filteredGroups.map((group) => {
                const active = selectedGroup?.id === group.id;
                return (
                  <button
                    key={group.id}
                    onClick={() => setSelectedGroup(group)}
                    className={`w-full rounded-2xl border bg-card p-4 text-left shadow-lg shadow-sky-500/5 portal-card-hover ${
                      active ? "border-primary/40 shadow-sky-500/10" : "border-border"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground">{group.displayName || "Unnamed group"}</div>
                        <div className="mt-1 truncate text-xs text-muted-foreground">{group.mail || group.mailNickname || "No mail alias"}</div>
                      </div>
                      {active && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="rounded-full bg-blue-500/10 px-2.5 py-1 text-xs font-medium text-blue-600">{active ? groupUsers.length : "-"} users</span>
                      <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-600">{group.securityEnabled ? "Security" : "Standard"}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className={panelClass}>
          {!selectedGroup ? (
            <EmptyState icon={Shield} title="Select a group" description="Choose an Azure group to inspect details and members." />
          ) : (
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{selectedGroup.displayName || "Unnamed group"}</h2>
                  <p className="mt-1 break-all text-xs text-muted-foreground">{selectedGroup.id}</p>
                </div>
                <div className="rounded-2xl bg-primary/10 px-3 py-2 text-xs font-semibold text-primary">
                  {groupUsers.length} members
                </div>
              </div>

              <div className="mb-4 flex flex-wrap gap-2">
                {[
                  { id: "details", label: "Details", icon: Info },
                  { id: "members", label: `Members (${groupUsers.length})`, icon: Users },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-all ${
                        activeTab === tab.id ? "bg-primary text-primary-foreground shadow-md shadow-primary/20" : "border border-border bg-card text-muted-foreground shadow-sm hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === "details" && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <DetailRow label="Group Name" value={selectedGroup.displayName} />
                  <DetailRow label="Mail" value={selectedGroup.mail} />
                  <DetailRow label="Mail Nickname" value={selectedGroup.mailNickname} />
                  <DetailRow label="Mail Enabled" value={selectedGroup.mailEnabled ? "Yes" : "No"} />
                  <DetailRow label="Visibility" value={selectedGroup.visibility} />
                  <DetailRow label="Security Enabled" value={selectedGroup.securityEnabled ? "Yes" : "No"} />
                  <DetailRow label="Security Identifier" value={selectedGroup.securityIdentifier} />
                  <DetailRow label="Created On" value={formatDate(selectedGroup.azureCreatedDateTime)} />
                  <div className="md:col-span-2">
                    <DetailRow label="Description" value={selectedGroup.description} />
                  </div>
                </div>
              )}

              {activeTab === "members" && (
                <div className="rounded-2xl border border-border bg-card p-3 shadow-lg shadow-sky-500/5">
                  <div className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 shadow-sm">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <input
                      className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      placeholder="Search members..."
                      value={memberSearch}
                      onChange={(event) => setMemberSearch(event.target.value)}
                    />
                  </div>

                  {usersLoading ? (
                    <SkeletonList />
                  ) : filteredUsers.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No members found</div>
                  ) : (
                    <div className="max-h-[430px] space-y-2 overflow-y-auto pr-1 scrollbar-thin">
                      {filteredUsers.map((user) => {
                        const active = selectedUser?.id === user.id;
                        return (
                          <button
                            key={user.id || user.azureId || user.displayName}
                            onClick={() => setSelectedUser(user)}
                            className={`w-full rounded-2xl border bg-card p-3 text-left shadow-lg shadow-sky-500/5 portal-card-hover ${
                              active ? "border-primary/40" : "border-border"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                                <User className="h-4 w-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-foreground">{user.displayName || "Unnamed user"}</div>
                                <div className="truncate text-xs text-muted-foreground">{user.mail || user.userPrincipalName || user.azureId}</div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        <section className={panelClass}>
          {selectedUser ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600">
                  <User className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold text-foreground">{selectedUser.displayName || "Unnamed user"}</h2>
                  <p className="truncate text-xs text-muted-foreground">{selectedUser.mail || selectedUser.userPrincipalName || selectedUser.azureId}</p>
                </div>
              </div>
              <DetailRow label="Surname" value={selectedUser.surname} />
              <DetailRow label="Azure ID" value={selectedUser.azureId || selectedUser.id} />
              <DetailRow label="Department" value={selectedUser.department} />
              <DetailRow label="Job Title" value={selectedUser.jobTitle} />
              <DetailRow label="Account Enabled" value={selectedUser.accountEnabled?.toString()} />
              <DetailRow label="Office Location" value={selectedUser.officeLocation} />
              <DetailRow label="Synced At" value={formatDate(selectedUser.synced_at)} />
              <div className="rounded-2xl border border-border bg-card p-3 shadow-lg shadow-sky-500/5">
                <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  Contact
                </div>
                <div className="break-words text-sm font-medium text-foreground">{selectedUser.mail || selectedUser.userPrincipalName || "N/A"}</div>
              </div>
            </div>
          ) : (
            <EmptyState icon={User} title="No member selected" description="Select a group member to view their Azure user summary." />
          )}
        </section>
      </div>

      <div className="sr-only" aria-live="polite">
        Selected subscription: {selectedSubscription || "none"}
      </div>
    </div>
  );
};

export default AzureGroups;
